import { useCallback, useEffect, useMemo, useState } from "react";
import { canPerform } from "@vital/shared";
import { useAuth, useActiveOrganization } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { InspectPanel } from "../components/InspectPanel";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import {
  AuditFilterBar,
  AuditInspectSummary,
  AuditInventoryList,
} from "../components/audit-inventory";
import { api, ApiError } from "../lib/api";
import {
  AUDIT_PAGE_NOTE,
  AUDIT_PRIVACY_NOTE,
  filterAndSortAudit,
  uniqueEntityTypes,
  type AuditListRow,
  type AuditSortKey,
} from "../lib/audit-ops";

export function AuditPage() {
  const { user } = useAuth();
  const activeOrg = useActiveOrganization();
  const authInput = {
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
  };
  const canRead = canPerform({ ...authInput, permission: "audit.read" });

  const organizationLabel =
    activeOrg.code || activeOrg.displayName || activeOrg.organizationId || "—";

  const [items, setItems] = useState<AuditListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const [appliedAction, setAppliedAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [entityFilter, setEntityFilter] = useState("all");
  const [sort, setSort] = useState<AuditSortKey>("time");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const limit = 25;

  const loadData = useCallback(async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api.listAudit({
        limit,
        offset,
        action: appliedAction || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, [offset, appliedAction, canRead]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedId && !items.some((i) => i.id === selectedId)) {
      setSelectedId(null);
    }
  }, [items, selectedId]);

  const filtered = useMemo(
    () =>
      filterAndSortAudit(items, {
        query,
        entityFilter,
        sort,
      }),
    [items, query, entityFilter, sort],
  );

  const entityOptions = useMemo(() => uniqueEntityTypes(items), [items]);

  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  );

  function applyActionFilter() {
    setOffset(0);
    setAppliedAction(actionFilter.trim());
  }

  if (!canRead) {
    return (
      <>
        <PageHeader title="سابقه تغییرات" />
        <p className="banner banner--error" role="alert">
          مجوز audit.read لازم است.
        </p>
      </>
    );
  }

  return (
    <>
      <PageHeader title="سابقه تغییرات" />

      <aside
        className="ops-safety-summary ops-safety-summary--privacy"
        role="note"
        data-vt-audit-safety
      >
        <p className="ops-safety-summary__eyebrow">حریم ممیزی</p>
        <ul className="ops-safety-summary__points">
          <li>فقط‌خواندنی و append-only — دامنهٔ سازمان فعلی</li>
          <li>فیلتر عمل API و صفحه‌بندی؛ جستجو روی صفحهٔ بارگذاری‌شده</li>
          <li>بدون IP، User-Agent، dump خام، وب‌هوک، یا راز</li>
        </ul>
        <details className="ops-safety-disclosure">
          <summary>متن کامل حریم ممیزی</summary>
          <p>{AUDIT_PAGE_NOTE}</p>
          <p>{AUDIT_PRIVACY_NOTE}</p>
        </details>
      </aside>

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}

      <div className="page-stack page-stack--inventory">
        <div
          className={`content-ops-layout${selected ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selected ? "true" : "false"}
        >
          <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
            <SectionHeader title="رویدادهای ممیزی" />
            <AuditFilterBar
              actionFilter={actionFilter}
              onActionFilterChange={setActionFilter}
              onApplyActionFilter={applyActionFilter}
              query={query}
              onQueryChange={setQuery}
              entityFilter={entityFilter}
              onEntityFilterChange={setEntityFilter}
              entityOptions={entityOptions}
              sort={sort}
              onSortChange={setSort}
              organizationLabel={organizationLabel}
            />

            {loading ? (
              <InventorySkeleton />
            ) : items.length === 0 ? (
              <OpsEmptyState title="رویدادی یافت نشد" />
            ) : filtered.length === 0 ? (
              <OpsEmptyState title="موردی در این فیلتر صفحه نیست" />
            ) : (
              <AuditInventoryList
                rows={filtered}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            )}

            <div className="pagination-bar">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={offset === 0 || loading}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                قبلی
              </button>
              <span className="ltr-isolate" dir="ltr">
                {total === 0 ? "0" : `${offset + 1}–${Math.min(offset + limit, total)}`} / {total}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={offset + limit >= total || loading}
                onClick={() => setOffset(offset + limit)}
              >
                بعدی
              </button>
            </div>
          </Card>
          {selected && (
            <InspectPanel title="جزئیات رویداد" className="content-inspect-panel">
              <AuditInspectSummary item={selected} organizationLabel={organizationLabel} />
            </InspectPanel>
          )}
        </div>
      </div>
    </>
  );
}
